import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RepairPartService } from '../../../services/repair-part.service';
import { ReplaySubject, firstValueFrom } from 'rxjs';

import { RouterModule } from '@angular/router';
import { RepairPartFilterForm } from '../../../form-model/repair-part-filter.form.model';
import '@maersk-global/mds-components-core/mc-multi-select';
import '@maersk-global/mds-components-core/mc-select';
import '@maersk-global/mds-components-core/mc-input';
import '@maersk-global/mds-components-core/mc-typeahead-multi-select';
import { AuthPolicies } from '../../../auth/auth-policies';

@Component({
  selector: 'app-repair-part-advance-filter',
  imports: [FormsModule, ReactiveFormsModule, RouterModule],
  templateUrl: './repair-part-advance-filter.component.html',
  styleUrl: './repair-part-advance-filter.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RepairPartAdvanceFilterComponent implements OnInit, OnDestroy {
  @Input({ required: true }) repairPartFilterForm!: FormGroup<RepairPartFilterForm>;
  @Input() bindMultiSelects: boolean = false;

  repairModeList: { label: string; value: string }[] = [];
  repairCodeTypeaheadData: { label: string; value: string }[] = [];
  partCodeTypeaheadData: { label: string; value: string }[] = [];

  selectedRepairCodes: { label: string; value: string }[] = [];
  selectedPartCodes: { label: string; value: string }[] = [];

  maxQuantityOperatorList = [
    { code: 'equals', description: 'Equals' },
    { code: 'above', description: 'Above' },
    { code: 'below', description: 'Below' },
    { code: 'equalorabove', description: 'Equal or above' },
    { code: 'equalorbelow', description: 'Equal or below' },
  ];

  isLoadingOptions: boolean = false;

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private readonly repairPartService: RepairPartService,
    private readonly formBuilder: FormBuilder,
    public readonly authPolicy: AuthPolicies,
  ) {}

  ngOnInit() {
    this.addControlsToTheFormGroup();
    this.loadAllFilterOptions();
    this.syncSelectedFromForm();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private syncSelectedFromForm(): void {
    const codeVal: string[] = this.repairPartFilterForm.get('repairCodeList')?.value ?? [];
    this.selectedRepairCodes = codeVal.map((v) => ({ label: v, value: v }));
    const partVal: string[] = this.repairPartFilterForm.get('partCodeList')?.value ?? [];
    this.selectedPartCodes = partVal.map((v) => ({ label: v, value: v }));
  }

  private addControlsToTheFormGroup() {
    if (!this.repairPartFilterForm.get('repairModeList')) {
      this.repairPartFilterForm.addControl('repairModeList', this.formBuilder.control<string[]>([]));
    }
    if (!this.repairPartFilterForm.get('repairCodeList')) {
      this.repairPartFilterForm.addControl('repairCodeList', this.formBuilder.control<string[]>([]));
    }
    if (!this.repairPartFilterForm.get('partCodeList')) {
      this.repairPartFilterForm.addControl('partCodeList', this.formBuilder.control<string[]>([]));
    }
    if (!this.repairPartFilterForm.get('maxQuantityOperator')) {
      this.repairPartFilterForm.addControl('maxQuantityOperator', this.formBuilder.control<string | null>(null));
    }
    if (!this.repairPartFilterForm.get('maxQuantityValue')) {
      this.repairPartFilterForm.addControl('maxQuantityValue', this.formBuilder.control<string | null>(null));
    }
    if (!this.repairPartFilterForm.get('applyRepairPartFilter')) {
      this.repairPartFilterForm.addControl('applyRepairPartFilter', this.formBuilder.control<boolean | null>(null));
    }
  }

  isControlInErrorState(control: FormControl): boolean | null {
    if (control.invalid && control.touched) {
      return true;
    }
    return null;
  }

  async loadAllFilterOptions() {
    this.isLoadingOptions = true;
    try {
      const [modes, codes, parts] = await Promise.all([
        firstValueFrom(this.repairPartService.getAllModes()),
        firstValueFrom(this.repairPartService.getAllCodes()),
        firstValueFrom(this.repairPartService.getAllParts()),
      ]);
      this.repairModeList = (modes || []).map((m: string) => ({ label: m, value: m }));
      this.repairCodeTypeaheadData = (codes || []).map((c: string) => ({ label: c, value: c }));
      this.partCodeTypeaheadData = (parts || []).map((p: string) => ({ label: p, value: p }));
    } catch (error) {
      console.error('Error loading filter options:', error);
      this.repairModeList = [];
      this.repairCodeTypeaheadData = [];
      this.partCodeTypeaheadData = [];
    } finally {
      this.isLoadingOptions = false;
    }
  }

  private mapDetailToItems(detail: any): Array<{ label: string; value: string }> {
    if (Array.isArray(detail?.value)) {
      return detail.value.map((s: any) => ({ label: s.label ?? s.value ?? s, value: s.value ?? s }));
    }
    if (Array.isArray(detail)) {
      return detail.map((s: any) => ({ label: s.label ?? s.value ?? s, value: s.value ?? s }));
    }
    if (detail) {
      return [{ label: detail.label ?? detail.value ?? detail, value: detail.value ?? detail }];
    }
    return [];
  }

  onRepairCodeSelected(event: Event): void {
    const detail = (event as CustomEvent).detail;
    const items = this.mapDetailToItems(detail);
    this.selectedRepairCodes = items;
    this.repairPartFilterForm.get('repairCodeList')?.setValue(items.map((s) => s.value));
  }

  onRepairCodeCleared(): void {
    this.selectedRepairCodes = [];
    this.repairPartFilterForm.get('repairCodeList')?.setValue([]);
  }

  onPartCodeSelected(event: Event): void {
    const detail = (event as CustomEvent).detail;
    const items = this.mapDetailToItems(detail);
    this.selectedPartCodes = items;
    this.repairPartFilterForm.get('partCodeList')?.setValue(items.map((s) => s.value));
  }

  onPartCodeCleared(): void {
    this.selectedPartCodes = [];
    this.repairPartFilterForm.get('partCodeList')?.setValue([]);
  }

  applyFilters() {
    this.repairPartFilterForm.get('applyRepairPartFilter')?.setValue(true);
  }

  clearFilters() {
    this.selectedRepairCodes = [];
    this.selectedPartCodes = [];
    this.repairPartFilterForm.reset({
      repairModeList: [],
      repairCodeList: [],
      partCodeList: [],
      maxQuantityOperator: null,
      maxQuantityValue: null,
    });
    this.repairPartFilterForm.get('applyRepairPartFilter')?.setValue(true);
  }
}
